import RequestModel from "../models/RequestModel";
import SearchParamModel from "../models/SearchParamModel";
import { getYYYYMMDD } from "../helpers/DateUtilities";
import RequestsSummaryModel from "../models/RequestsSummaryModel";
import ReportParamModel from "../models/ReportParamModel";
import {SearchCriteriaEnum} from "../helpers/Enums";
import {IHttpRequestService} from "./HttpRequestService";
import { addDaysToDate } from "../helpers/DateUtilities";
import { prepareRequestModel } from "../helpers/RequestModelUtilities";

export interface IRequestService {
    GetById(id:number ):Promise<RequestModel|undefined>;
    GetDueDate(startDate:Date, businessDays:number):Promise<Date>
    GetBusinessDaysBetweenDates(startDate:Date, endDate:Date):Promise<number>
    SearchBy(searchParams:SearchParamModel|undefined ):Promise<RequestModel[]>;
    GenerateReportBy(searchParams:ReportParamModel|undefined ):Promise<unknown[]>;
    GetAll( ):Promise<RequestModel[]>;
    GetRequestsSummary():Promise<RequestsSummaryModel|undefined>;
    Save(data:RequestModel):Promise<RequestModel|undefined>;
}

export class RequestService implements IRequestService {
    constructor(private httpRequestService: IHttpRequestService) {}

    private DownloadAndOpenFile(fileData: Blob, fileName: string){
        const pdfBlob = new Blob([fileData], {type: 'application/pdf'});
        const pdfUrl = URL.createObjectURL(pdfBlob);

        window.open(pdfUrl, '_blank');

        const fileLink = document.createElement('a');
        fileLink.href = pdfUrl;
        fileLink.download = fileName;
        fileLink.click();
        fileLink.remove();

        window.URL.revokeObjectURL(pdfUrl);
    }

    public async GetById(id:number ):Promise<RequestModel|undefined>{
        try{  
            if(id>0)
            {  
                const { data } = await this.httpRequestService.Get<RequestModel>(`/api/v1/Request/${id}`);
                return data? prepareRequestModel(data):data;
            }     
            return new RequestModel();
        }catch(e)
        {
            this.httpRequestService.HandleError(e);
        }
        
    }

    public async GetDueDate(startDate:Date, businessDays:number):Promise<Date>{
        const defaultDueDate = addDaysToDate(startDate,businessDays);
        const formattedStartDate = getYYYYMMDD(startDate);
        try{
            const { data } = await this.httpRequestService.Get<Date>(`/api/v1/Request/DueDate/${formattedStartDate}/${businessDays}`);
            return data? data : defaultDueDate;
        }catch(e)
        {
            this.httpRequestService.HandleError(e);
        }

        return defaultDueDate;
    }

    public async GetBusinessDaysBetweenDates(startDate:Date, endDate:Date):Promise<number>{
        const formattedStartDate = getYYYYMMDD(startDate);
        const formattedEndDate = getYYYYMMDD(endDate);
        try{
            const { data } = await this.httpRequestService.Get<number>(`/api/v1/Request/BusinessDays/${formattedStartDate}/${formattedEndDate}`);
            return data || 0;
        }catch(e)
        {
            this.httpRequestService.HandleError(e);
        }

        return 0;
    }
  
    public async SearchBy(searchParams:SearchParamModel|undefined ):Promise<RequestModel[]>{
        if(searchParams && searchParams.searchType)
        {
            try{   
                let params=`SearchType=${Number(searchParams.searchType)}`;
                
                    if(searchParams.searchType== SearchCriteriaEnum.DateRange)
                    {
                        params+=`&ByDateType=${searchParams.byDateType}`; 
                        if(searchParams.byDateFrom)
                            params+=`&ByDateFrom=${getYYYYMMDD(new Date(searchParams.byDateFrom))}`; 
                        if(searchParams.byDateTo)
                            params+=`&ByDateTo=${getYYYYMMDD(new Date(searchParams.byDateTo))}`; 
                    }
                    else if(searchParams.searchType== SearchCriteriaEnum.Applicant)
                    {
                        params+=`&ByApplicantName=${searchParams.byApplicantName}`; 
                    }
                    else if(searchParams.searchType==SearchCriteriaEnum.FileNumber)
                    {
                        params+=`&ByFileNumber=${searchParams.byFileNumber}`;  
                    }
                    else if(searchParams.searchType== SearchCriteriaEnum.Keyword)
                    {
                        params+=`&ByKeywordTxt=${encodeURIComponent( (searchParams.byKeywordTxt??'').replace("https://","https_//").replace("http://","http_//") )}`; 
                    }
                    else if(searchParams.searchType== SearchCriteriaEnum.RequestType)
                    {
                        params+=`&ByRequestTypeId=${Number(searchParams.byRequestTypeId)}`; 
                    }
                    else if(searchParams.searchType== SearchCriteriaEnum.ActiveRequests ||
                            searchParams.searchType== SearchCriteriaEnum.CompletedRequests)
                    {
                        // The server infers these filters from SearchType alone.
                    }
                    
                const url=`/api/v1/Request/Search?${params}`;
                const { data } = await this.httpRequestService.Get<RequestModel[]>(url);
                if(data)
                {
                    for (let i = 0; i < data.length; i++) {
                        data[i]= prepareRequestModel(data[i]);
                      }
                } 
                return data as RequestModel[]; 
            }catch(e)
            {
                this.httpRequestService.HandleError(e);
            }
        }
        return [];   
    }

    public async GenerateReportBy(searchParams:ReportParamModel|undefined ):Promise<unknown[]>{
        if (searchParams) {
            try {
                const dateFrom = getYYYYMMDD(new Date(searchParams.ByDateFrom!));
                const dateTo = getYYYYMMDD(new Date(searchParams.ByDateTo!));
                const fileName = `FormalReport-From_${dateFrom}-To_${dateTo}.pdf`;

                const url = `/api/v1/Request/GenerateRequestsSummaryReport/${dateFrom}/${dateTo}/${searchParams.ReportTitle}`;
                const response = await this.httpRequestService.GetFile(url);

                this.DownloadAndOpenFile(response.data, fileName);
            } catch (e) {
                this.httpRequestService.HandleError(e);
            }
        }
        return [];   
    }

    public async GetAll( ):Promise<RequestModel[]>{
        
             try{  
                 const { data } = await this.httpRequestService.Get<RequestModel[]>(`/api/v1/Request/GetAll`);
                 
                 if(data)
                 {
                     for (let i = 0; i < data.length; i++) {
                         data[i]= prepareRequestModel(data[i]);
                       }
                 } 
                 return data as RequestModel[]; 
             }catch(e)
             {
                 this.httpRequestService.HandleError(e);
             }
        
         return [];   
    }

    public async GetRequestsSummary():Promise<RequestsSummaryModel|undefined>{
        
        try{  
            const { data } = await this.httpRequestService.Get<RequestsSummaryModel>(`/api/v1/Request/GetRequestsSummary`);
            const summaryData=data as RequestsSummaryModel;
            if(summaryData)
            {
                for (let i = 0; i < summaryData.onTrackRequests.length; i++) 
                    summaryData.onTrackRequests[i]= prepareRequestModel(summaryData.onTrackRequests[i]);
                for (let i = 0; i < summaryData.dueWeekRequests.length; i++) 
                    summaryData.dueWeekRequests[i]= prepareRequestModel(summaryData.dueWeekRequests[i]);
                for (let i = 0; i < summaryData.overdueRequests.length; i++) 
                    summaryData.overdueRequests[i]= prepareRequestModel(summaryData.overdueRequests[i]);
                for (let i = 0; i < summaryData.onHoldRequests.length; i++) 
                    summaryData.onHoldRequests[i]= prepareRequestModel(summaryData.onHoldRequests[i]);
            }  
            return summaryData; 
        }catch(e)
        {
            this.httpRequestService.HandleError(e);
        }   
        return undefined;   
    }

    public async Save(data:RequestModel):Promise<RequestModel|undefined>{
        try{
            if(!data.disclosureTypeId ) data.disclosureTypeId=null;
            if(!data.dispositionTypeId) data.dispositionTypeId=null;
            if(!data.releaseStatusTypeId) data.releaseStatusTypeId=null;
            if(! data.deliveryMethodTypeId)data.deliveryMethodTypeId=null; 
            if(data.sharepointUrl) data.sharepointUrl=encodeURIComponent(data.sharepointUrl);

            const response  =
            data.id>0 ?
                await this.httpRequestService.Put<RequestModel, RequestModel>(`/api/v1/Request/Update`,data):
                await this.httpRequestService.Post<RequestModel, RequestModel>(`/api/v1/Request/Add`,data);
            
            if(response)
                return response as RequestModel;
        }catch(e)
        {
            this.httpRequestService.HandleError(e);
        }
        return undefined;
    }
  };
